import { useTranslation } from 'react-i18next';

import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { closeModal } from '@mantine/modals';
import { handleSubmitError } from '@utils/forms';

import {
  CreateCustomerAddressDto,
  CustomerAddress,
} from '@interfaces/address.interface';
import { Customer } from '@interfaces/customer.interface';

import {
  useCreateCustomerAddressMutation,
  useUpdateCustomerAddressMutation,
} from '@api/customerAddress.api';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from './common/locations';

export const UPSERT_CUSTOMER_ADDRESS_MODAL_ID = 'upsert-customer-address';

interface UpsertCustomerAddressProps {
  customer?: Customer;
  customerAddress?: CustomerAddress;
}

export default function UpsertCustomerAddress({
  customer,
  customerAddress,
}: UpsertCustomerAddressProps) {
  const { t } = useTranslation();

  // ==========================================================================
  // Api
  // ==========================================================================
  const [createCustomerAddress, { isLoading: isLoadingCreateCustomerAddress }] =
    useCreateCustomerAddressMutation();
  const [updateCustomerAddress, { isLoading: isLoadingUpdateCustomerAddress }] =
    useUpdateCustomerAddressMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    firstName: customerAddress?.firstName || '',
    lastName: customerAddress?.lastName || '',
    company: customerAddress?.company || '',
    department: customerAddress?.department || '',
    address: customerAddress?.address || '',
    postalCode: customerAddress?.postalCode || '',
    city: customerAddress?.city || '',
    province: customerAddress?.province || '',
    country: customerAddress?.country || '',
    phone: customerAddress?.phone || '',
  };

  const form = useForm({
    initialValues,
  });

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleCopyAddress = () => {
    form.setValues({
      firstName: customer?.user.firstName || '',
      lastName: customer?.user.lastName || '',
      company: customer?.company || '',
      department: customer?.department || '',
      address: customer?.address || '',
      postalCode: customer?.postalCode || '',
      city: customer?.city || '',
      province: customer?.province || '',
      country: customer?.country || '',
      phone: customer?.phone || '',
    });
  };

  const onSubmit = async (values: typeof initialValues) => {
    try {
      const mappedValues: CreateCustomerAddressDto = {
        firstName: values.firstName,
        lastName: values.lastName,
        company: values.company,
        department: values.department.trim() !== '' ? values.department : null,
        address: values.address,
        postalCode: values.postalCode,
        city: values.city,
        province: values.province,
        country: values.country,
        phone: values.phone.trim() !== '' ? values.phone : null,
      };

      if (customerAddress) {
        await updateCustomerAddress({
          id: customerAddress.id,
          body: mappedValues,
        }).unwrap();
      } else {
        await createCustomerAddress(mappedValues).unwrap();
      }

      closeModal(UPSERT_CUSTOMER_ADDRESS_MODAL_ID);
    } catch (error) {
      handleSubmitError(error, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        {!customerAddress && (
          <Button onClick={handleCopyAddress}>
            {t('address.copyBillingAddress')}
          </Button>
        )}
        <TextInput
          label={t('address.firstName')}
          placeholder={t('address.firstNamePlaceholder')}
          required
          {...form.getInputProps('firstName')}
        />
        <TextInput
          label={t('address.lastName')}
          placeholder={t('address.lastNamePlaceholder')}
          required
          {...form.getInputProps('lastName')}
        />

        <TextInput
          label={t('address.company')}
          placeholder={t('address.companyPlaceholder')}
          required
          {...form.getInputProps('company')}
        />

        <TextInput
          label={t('address.department')}
          placeholder={t('address.departmentPlaceholder')}
          {...form.getInputProps('department')}
        />

        <TextInput
          label={t('address.phone')}
          placeholder={t('address.phonePlaceholder')}
          {...form.getInputProps('phone')}
        />

        <NationSelector required {...form.getInputProps('country')} />

        {form.values.country === 'Italia' ? (
          <ProvinceSelector required {...form.getInputProps('province')} />
        ) : (
          <TextInput
            label={t('address.province')}
            placeholder={t('address.provincePlaceholder')}
            required
            {...form.getInputProps('province')}
          />
        )}
        {form.values.country === 'Italia' ? (
          <CitySelector
            province={form.values.province}
            disabled={form.values.province === ''}
            required
            {...form.getInputProps('city')}
          />
        ) : (
          <TextInput
            label={t('address.city')}
            placeholder={t('address.cityPlaceholder')}
            required
            {...form.getInputProps('city')}
          />
        )}

        {form.values.country === 'Italia' ? (
          <ZipSelector
            city={form.values.city}
            disabled={form.values.city === ''}
            required
            {...form.getInputProps('postalCode')}
          />
        ) : (
          <TextInput
            label={t('address.postalCode')}
            placeholder={t('address.postalCodePlaceholder')}
            required
            {...form.getInputProps('postalCode')}
          />
        )}
        <TextInput
          label={t('address.address')}
          placeholder={t('address.addressPlaceholder')}
          required
          {...form.getInputProps('address')}
        />
      </Stack>

      {form.errors.general && (
        <Text c="red" size="sm" ta="right" mt="lg">
          {form.errors.general}
        </Text>
      )}

      <Group justify="end" mt="lg">
        <Button
          variant="default"
          onClick={() => closeModal(UPSERT_CUSTOMER_ADDRESS_MODAL_ID)}
          loading={
            isLoadingCreateCustomerAddress || isLoadingUpdateCustomerAddress
          }
        >
          {t('cart.cancel')}
        </Button>
        <Button
          type="submit"
          loading={
            isLoadingCreateCustomerAddress || isLoadingUpdateCustomerAddress
          }
        >
          {t('cart.confirm')}
        </Button>
      </Group>
    </form>
  );
}
